// 侧边导航：代理商后台 · /agent/* 正式路由
// 菜单数据源抽至 src/menu.tsx（P2 对齐 admin 模式）；本组件只负责渲染与动态徽章挂接。
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { MENU, setLeafBadge } from '@/menu';
import { fetchNoticeUnreadCount } from '@/api/notice';
import { useNoticeSound } from '@/hooks/useNoticeSound';

/** 异常与通知叶子的动态徽章挂接点（path 键 = 冻结表壳基路径口径） */
const NOTICE_BADGE_PATH = '/agent/settlement/notices';

export default function Sidebar() {
  // 通知提示音（60s 轮询，可信增加即响；单实例挂载点，勿在 App.tsx 等处重复挂载）
  useNoticeSound();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [noticeBadge, setNoticeBadge] = useState<number>(0);

  useEffect(() => {
    let alive = true;
    void fetchNoticeUnreadCount().then((res) => {
      if (alive && res.ok) setNoticeBadge(res.data.unreadCount ?? 0);
    });
    return () => {
      alive = false;
    };
  }, []);

  // 通知未读数 > 0 时挂红点徽章，否则隐藏（setLeafBadge 返回新数组，不改 MENU 常量）
  const menu = useMemo(
    () => setLeafBadge(MENU, NOTICE_BADGE_PATH, noticeBadge > 0 ? noticeBadge : undefined),
    [noticeBadge],
  );

  const toggle = (label: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <aside className="w-60 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0 shadow-sm">
      <div className="px-5 py-5 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4 text-white"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
              <path d="M2 12h20" />
            </svg>
          </div>
          <div>
            <p className="text-slate-900 font-bold text-sm leading-tight">Allinks</p>
            <p className="text-slate-400 text-xs">代理商展业后台</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-3">
        {menu.map((item) => {
          const isCollapsed = collapsed.has(item.label);
          if (!item.leaves && item.path) {
            return (
              <div key={item.label} className="mb-1">
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-brand-50 text-brand-700 border border-brand-200'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
                    }`
                  }
                >
                  {item.icon}
                  <span className="flex-1 text-left">{item.label}</span>
                </NavLink>
              </div>
            );
          }
          return (
            <div key={item.label} className="mb-1">
              <button
                onClick={() => toggle(item.label)}
                className="w-full flex items-center justify-between px-2 py-1.5 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <span className="text-[10px] font-semibold uppercase tracking-wider">{item.label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
              </button>
              {!isCollapsed && item.leaves && (
                <div className="space-y-0.5 mt-1">
                  {item.leaves.map((leaf) => (
                    <NavLink
                      key={leaf.path}
                      to={leaf.path}
                      className={({ isActive }) =>
                        `w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all group ${
                          isActive
                            ? 'bg-brand-50 text-brand-700 border border-brand-200'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-transparent'
                        }`
                      }
                    >
                      {leaf.icon || <span className="text-slate-400">●</span>}
                      <span className="flex-1 text-left">{leaf.label}</span>
                      {leaf.badge ? (
                        <span className="min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] text-white font-medium flex items-center justify-center">
                          {leaf.badge}
                        </span>
                      ) : null}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="px-3 py-3 border-t border-slate-100">
        <div className="bg-slate-50 rounded-lg px-3 py-2.5 flex items-center gap-2">
          <div className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 border border-indigo-200">
            <span className="text-indigo-600 text-xs font-bold">AG</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-slate-800 text-xs font-medium truncate">代理商账号</p>
            <p className="text-slate-400 text-[10px] truncate">一级代理 · /agent</p>
          </div>
        </div>
      </div>
    </aside>
  );
}
